import React from 'react';
import {
  Container,
  Card,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  ListGroup,
} from 'react-bootstrap';
import {useSelector} from 'react-redux';
import type {RootState} from '../store';
import {type VersionInfo, fetchVersionInfo} from '../libs/version';

export default function VersionPage(): React.JSX.Element {
  const current = useSelector((state: RootState) => state.version.data);
  const [latest, setLatest] = React.useState<VersionInfo | undefined>(
    undefined,
  );
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const fetchLatest = React.useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await fetchVersionInfo();
      setLatest(data);
    } catch {
      setError('Failed to fetch latest version');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void fetchLatest();
  }, [fetchLatest]);

  const isOutdated =
    Boolean(current?.buildId) &&
    Boolean(latest?.buildId) &&
    current?.buildId !== latest?.buildId;

  return (
    <Container className='my-5'>
      <h1>App Version</h1>

      <Row className='gy-3'>
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Currently loaded (this tab)</Card.Title>
              <ListGroup variant='flush'>
                <ListGroup.Item>
                  <strong>Build ID:</strong>{' '}
                  <code>{current?.buildId ?? 'unknown'}</code>
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Build Time:</strong> {current?.buildTime ?? 'unknown'}
                </ListGroup.Item>
              </ListGroup>
              {!current?.buildId && (
                <Alert variant='warning' className='mt-3'>
                  Current build ID is not available yet. Ensure the Redux store
                  has loaded VersionInfo.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title>Latest available (server)</Card.Title>
              {loading ? (
                <div className='d-flex align-items-center'>
                  <Spinner animation='border' size='sm' className='me-2' />
                  <span>Checking latest version…</span>
                </div>
              ) : error ? (
                <Alert variant='danger' className='mb-0'>
                  {error}
                </Alert>
              ) : (
                <ListGroup variant='flush'>
                  <ListGroup.Item>
                    <strong>Build ID:</strong>{' '}
                    <code>{latest?.buildId ?? 'unknown'}</code>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Build Time:</strong>{' '}
                    {latest?.buildTime ?? 'unknown'}
                  </ListGroup.Item>
                </ListGroup>
              )}
              <div className='mt-3'>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  onClick={() => {
                    void fetchLatest();
                  }}
                  disabled={loading}
                >
                  {loading ? 'Rechecking…' : 'Recheck'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className='mt-3'>
        <Card.Body>
          <Card.Title>Status</Card.Title>
          {isOutdated ? (
            <Alert variant='info' className='mb-3'>
              A newer version is available. Refresh to load the latest app.
            </Alert>
          ) : (
            <Alert variant='success' className='mb-3'>
              You are running the latest version.
            </Alert>
          )}
          <Button
            variant='primary'
            onClick={() => {
              try {
                globalThis.location.reload();
              } catch {
                // Reload failed, ignore
              }
            }}
          >
            Refresh
          </Button>
        </Card.Body>
      </Card>
    </Container>
  );
}
